import { Link2, Users } from "lucide-react";
import { useCallback, useId } from "react";
import { toast } from "sonner";

import { useTranslation } from "@/core/i18n";

import { useAgelessMarketingSetupLink } from "./useAgelessMarketingSetupLink";

type AgelessMarketingShareBannerProps = {
  readonly platformId: string;
};

/**
 * Tells an admin the thing they would otherwise never discover: this page can be handed to
 * someone without an Ageless account, and here is the link. A single strip above the page
 * title; rendered only for signed-in admins — a visitor who arrived through a share link is
 * already inside the flow it describes.
 */
export function AgelessMarketingShareBanner({
  platformId,
}: AgelessMarketingShareBannerProps): React.ReactElement {
  const { t } = useTranslation();
  const linkFieldId = useId();
  const { ensureSetupLink, isMinting, setupLinkUrl } = useAgelessMarketingSetupLink(platformId);

  const handleCopy = useCallback(async () => {
    let url: string;
    try {
      url = await ensureSetupLink();
    } catch {
      toast.error(t("agelessMarketing.shareBanner.copyFailed"));
      return;
    }

    // The link is rendered below regardless, so a blocked or unavailable clipboard degrades to
    // "select it yourself" rather than losing the link the admin just paid a request for.
    try {
      await globalThis.navigator.clipboard.writeText(url);
      toast.success(t("agelessMarketing.shareBanner.copied"));
    } catch {
      toast.error(t("agelessMarketing.shareBanner.copyFailed"));
    }
  }, [ensureSetupLink, t]);

  return (
    <div
      className="flex flex-col gap-3 rounded-xl border border-foreground/10 bg-ageless-champagne-white px-4 py-3"
      data-testid="AgelessMarketingShareBanner"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <Users className="h-5 w-5 shrink-0 text-newTheme-ageless-primary-hover" />
          <p className="type-body-sm text-foreground">
            <span>{t("agelessMarketing.shareBanner.title")}</span>{" "}
            {t("agelessMarketing.shareBanner.body")}
          </p>
        </div>

        <button
          className="inline-flex min-h-9 shrink-0 items-center justify-center gap-1.5 rounded-md border border-foreground/15 bg-white px-4 py-1.5 type-label-md text-newTheme-ageless-primary-hover hover:bg-newTheme-ageless-surface disabled:opacity-60"
          disabled={isMinting}
          type="button"
          onClick={handleCopy}
        >
          <Link2 className="h-4 w-4" />
          {isMinting
            ? t("agelessMarketing.shareBanner.creating")
            : t("agelessMarketing.shareBanner.copy")}
        </button>
      </div>

      {setupLinkUrl && (
        <div className="flex flex-col gap-2">
          <label className="type-body-xs font-medium text-secondary" htmlFor={linkFieldId}>
            {t("agelessMarketing.shareBanner.linkLabel")}
          </label>
          <input
            readOnly
            className="w-full rounded-md border bg-white px-3 py-2 type-body-sm"
            id={linkFieldId}
            value={setupLinkUrl}
            onFocus={(event) => event.currentTarget.select()}
          />
          <p className="type-body-xs text-secondary">{t("agelessMarketing.shareBanner.caution")}</p>
        </div>
      )}
    </div>
  );
}
